import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Controller } from 'react-hook-form';
import {
  ButtonSecondary,
  ButtonSize,
  formatBalance,
  IconExpand,
  Select,
  SelectSize,
} from '@ui/src';
import { DictionaryItem } from '../../../../hoc/new-application/types';
import { SourcesOfIncomeTypes } from './types';
import AdditionalInfoBlock from '../additional-info-block';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';
import { CustomerStatus } from '../../../../hoc/customers/types';

import cn from '../aml.module.css';
import SourceOfIncomeItem from './source-of-income';

const SourcesOfIncome = ({
  sourcesOfIncome,
  fields,
  control,
  clearErrors,
  setValue,
  status,
  errors,
  baseCurrency,
}: SourcesOfIncomeTypes) => {
  const [sourceOfIncomeFiltered, setSourceOfIncomeFiltered] =
    useState<DictionaryItem[]>();
  const [totalValue, setTotalValue] = useState(0);

  useEffect(() => {
    if (sourcesOfIncome && fields?.sourcesOfIncomes) {
      setSourceOfIncomeFiltered(
        sourcesOfIncome?.filter((item) =>
          (
            fields?.sourcesOfIncomes as { sourceId: string; value: string }[]
          )?.every((income) => item.id !== income.sourceId),
        ),
      );
    }
  }, [fields?.sourcesOfIncomes, sourcesOfIncome]);

  useEffect(() => {
    if (
      (
        fields?.sourcesOfIncomes as { sourceId: string; value: string }[]
      )?.filter((item) => item.sourceId)?.length
    ) {
      let totalSourceOfIncome = 0;

      (fields?.sourcesOfIncomes as { sourceId: string; value: string }[])
        ?.filter((item) => item.sourceId)
        ?.forEach((item) => {
          totalSourceOfIncome += Number(item.value);
        });

      setTotalValue(totalSourceOfIncome);
    }
  }, [fields?.sourcesOfIncomes]);

  return (
    <AdditionalInfoBlock title="Source of income">
      <RegulatedCompanyRow title="Require">
        <Controller
          control={control}
          name="requiresSourcesOfIncomes"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                clearErrors(['requiresSourcesOfIncomes']);

                if (e.target.value === 'no') {
                  clearErrors([
                    'sourcesOfIncomes',
                    'sourcesOfIncomesBorrowedOther',
                    'sourcesOfIncomesOther',
                  ]);

                  setValue('sourcesOfIncomes', []);
                  setValue('sourcesOfIncomesBorrowedOther', '');
                  setValue('sourcesOfIncomesOther', '');
                }
              }}
              selectWrapperClassName={
                status === CustomerStatus.CLOSED ? cn.selectDisabled : ''
              }
              disabled={status === CustomerStatus.CLOSED}
              tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
              error={errors?.requiresSourcesOfIncomes?.message}
              fullWidth
              borderless
            />
          )}
        />
      </RegulatedCompanyRow>
      {fields?.requiresSourcesOfIncomes === 'yes' && (
        <>
          <div className={cn.sourceOfIncomeTitle}>
            Mandatory for high-risk cases only
          </div>
          {(fields?.sourcesOfIncomes?.length as number) > 0 && (
            <div className={cn.sourcesOfIncomeList}>
              {(
                fields?.sourcesOfIncomes as {
                  sourceId: string;
                  value: string;
                  id?: string;
                }[]
              )?.map((item) => (
                <SourceOfIncomeItem
                  key={item?.id || item.sourceId}
                  sourcesOfIncome={sourcesOfIncome}
                  sourceOfIncomeFiltered={sourceOfIncomeFiltered}
                  sourceOfIncome={{
                    id: item?.id || item.sourceId,
                    ...item,
                  }}
                  sourceOfIncomeValues={
                    fields?.sourcesOfIncomes as {
                      sourceId: string;
                      value: string;
                    }[]
                  }
                  setValue={setValue}
                  baseCurrency={baseCurrency}
                  control={control}
                  errors={errors}
                  clearErrors={clearErrors}
                  status={status}
                />
              ))}
            </div>
          )}

          {(
            fields?.sourcesOfIncomes as { sourceId: string; value: string }[]
          )?.filter((item) => item.sourceId)?.length > 0 && (
            <div className={cn.sourceOfIncomeTotal}>
              {`Total Annual Income: ${formatBalance(totalValue, baseCurrency)} ${baseCurrency?.name}`}
            </div>
          )}
          {sourceOfIncomeFiltered &&
            sourceOfIncomeFiltered?.length > 0 &&
            status !== CustomerStatus.CLOSED && (
              <div className={cn.addSourceButtonWrapper}>
                <ButtonSecondary
                  text="Add Source"
                  icon={<IconExpand width={16} height={16} />}
                  size={ButtonSize.SMALL}
                  className={`button-white ${cn.addSourceButton}`}
                  onClick={() => {
                    const nextSourceOfIncome = (
                      [] as {
                        sourceId: string;
                        value: string | number;
                        id?: string;
                      }[]
                    ).concat(
                      fields?.sourcesOfIncomes as {
                        id?: string;
                        sourceId: string;
                        value: string | number;
                      }[],
                    );

                    nextSourceOfIncome.push({
                      id: `source-of-income-${uuid()}`,
                      sourceId: '',
                      value: '',
                    });

                    setValue('sourcesOfIncomes', nextSourceOfIncome);
                  }}
                />
              </div>
            )}
        </>
      )}
      <Controller
        control={control}
        name="sourcesOfIncomes"
        defaultValue={[]}
        render={({ field: { name } }) => <input type="hidden" name={name} />}
      />

      {errors?.sourcesOfIncomes?.message && (
        <div className={cn.stepOneQuestionnaireError}>
          {errors?.sourcesOfIncomes?.message}
        </div>
      )}
    </AdditionalInfoBlock>
  );
};

export default SourcesOfIncome;
